import React, { useEffect } from "react";
import {
  Formik,
  Form,
  Field,
  ErrorMessage as FormikErrorMessage,
} from "formik";
import * as Yup from "yup";
import "./applicationForm.css";
import SubmitButton from "../submitButton/submitButton";
import FaSpinner from "../faSpinner/faSpinner";
import ErrorMessage from "../errorMessage/errorMessage";
import TagField from "../tagField/tagField";
import Checkbox from "../checkbox/checkbox";
import { COLOR_PALETTE } from "../../constants/colors";
import { FRONTEND_ORIGIN_URL } from "../../config";
import CopyAndPasteToClipBoard from "../copyAndPasteToClipBoard/copyAndPasteToClipBoard";

// Validation schema using Yup
const validationSchema = Yup.object()
  .shape({
    title: Yup.string().required("Required"),
    description: Yup.string().max(1000, "Must be 1000 characters or less"),
    authorizationCodeAllowed: Yup.boolean(),
    clientCredentialsAllowed: Yup.boolean(),
  })
  .test(
    "at-least-one-grant-type",
    "You must select at least one Grant Type",
    function (values) {
      return values.authorizationCodeAllowed || values.clientCredentialsAllowed;
    }
  );

const ApplicationForm = (props) => {
  const {
    loading,
    createApplication,
    errorMessage,
    editing,
    editApplication,
    applicationStatus,
    initialValues = {},
  } = props;

  const updateOauthTags = () => {
    const defaultUrl = `${FRONTEND_ORIGIN_URL}oauth2/redoc-callback`;
    const duplicateUrl = props.oAuthTagsValue.includes(defaultUrl);

    if (duplicateUrl) return null;

    return props.oAuthTagsUpdate([...props.oAuthTagsValue, defaultUrl]);
  };

  const isApplicationStatusProd = applicationStatus === "PRODUCTION";

  // Prepare initial values
  const formInitialValues = {
    title: initialValues.title || "",
    description: initialValues.description || "",
    authorizationCodeAllowed: initialValues.authorizationCodeAllowed || false,
    clientCredentialsAllowed: initialValues.clientCredentialsAllowed || false,
    applicationUuid: initialValues.applicationUuid || "",
    subscriptionIds: initialValues.subscriptionIds || [],
  };

  const handleSubmit = (values, { setSubmitting, setErrors }) => {
    const submitData = {
      ...values,
      oAuthRedirectUri: props.oAuthTagsValue,
      postLogoutRedirectUri: props.postLogoutTagsValue,
    };

    // Call the appropriate action
    if (editing) {
      editApplication(submitData);
    } else {
      createApplication(submitData);
    }

    // Reset submitting state after a delay to handle async operations
    // The actual state will be managed by the loading prop
    setTimeout(() => {
      setSubmitting(false);
    }, 500);
  };

  // Reset form submission state when loading changes to false (operation complete)
  useEffect(() => {
    if (!loading && errorMessage) {
      // Error occurred, form should be re-enabled
    }
  }, [loading, errorMessage]);

  return (
    <div>
      <Formik
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting,
          setFieldValue,
        }) => (
          <Form>
            <div>
              <div>
                <label className="block__form_element_label">
                  Title *{" "}
                  {touched.title && errors.title && (
                    <ErrorMessage message={errors.title} inline />
                  )}
                </label>
                <Field
                  className="body_field"
                  name="title"
                  placeholder="Enter the title of your application"
                  type="text"
                  disabled={isApplicationStatusProd}
                />
              </div>

              <div style={{ marginTop: "16px" }}>
                <label className="block__form_element_label">
                  Description
                  {touched.description && errors.description && (
                    <ErrorMessage message={errors.description} inline />
                  )}
                </label>
                <Field
                  as="textarea"
                  className="body_field"
                  name="description"
                  placeholder="Enter a short description for your application"
                  style={{ minHeight: "100px", resize: "vertical" }}
                />
              </div>

              <div
                className="field_checkbox"
                style={{ width: "100%", marginTop: "16px" }}
              >
                <label className="block__form_element_label">
                  Allowed Grant Types *
                </label>

                <div style={{ marginBottom: "8px" }}>
                  <Checkbox
                    name="authorizationCodeAllowed"
                    id="authorizationCodeAllowed"
                    checked={values.authorizationCodeAllowed}
                    onChange={(e) => {
                      setFieldValue(
                        "authorizationCodeAllowed",
                        e.target.checked
                      );
                    }}
                    label="Authorization Code"
                    disabled={isApplicationStatusProd}
                  />
                </div>

                <div>
                  <Checkbox
                    name="clientCredentialsAllowed"
                    id="clientCredentialsAllowed"
                    checked={values.clientCredentialsAllowed}
                    onChange={(e) => {
                      setFieldValue(
                        "clientCredentialsAllowed",
                        e.target.checked
                      );
                    }}
                    label="Client Credentials"
                    disabled={isApplicationStatusProd}
                  />
                </div>

                {!values.authorizationCodeAllowed &&
                  !values.clientCredentialsAllowed &&
                  (touched.authorizationCodeAllowed ||
                    touched.clientCredentialsAllowed) && (
                    <ErrorMessage message="You must select at least one Grant Type" />
                  )}
              </div>

              <TagField
                label="OAuth Redirect URI"
                placeholder="Enter Uri"
                value={props.oAuthTagsValue}
                onChange={props.oAuthTagsUpdate}
              />

              <p>
                For use with NBG developer portal documentation add:{" "}
                <em style={{ color: COLOR_PALETTE.yellow.light }}>
                  {FRONTEND_ORIGIN_URL}oauth2/redoc-callback
                </em>
                <CopyAndPasteToClipBoard
                  onClick={() => updateOauthTags()}
                  popover
                  message={"Copy and Paste to OAuth Redirect URI"}
                  popoverDirection="bottom"
                />
              </p>
              <br />

              <TagField
                label="Post Logout uri"
                placeholder="Enter Uri"
                value={props.postLogoutTagsValue}
                onChange={props.postLogoutUpdate}
              />
            </div>

            <div style={{ textAlign: "center", marginTop: 20 }}>
              {loading && <FaSpinner loading={true} />}
              {errorMessage && <ErrorMessage message={errorMessage} />}
              <SubmitButton
                disabled={loading || isSubmitting}
                title="SUBMIT"
                position="right"
                classname="app-submit-button btn"
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ApplicationForm;
